class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Force a reflow so the browser commits the drawer's pre-open state
    // (transform: translateX(100%), visibility: hidden) before the active
    // class flips it to translateX(0). Without this, both states can batch
    // into the same paint and the transition doesn't fire.
    this.classList.add('animate');
    void this.offsetWidth;
    this.classList.add('active');

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState, options) {
    // async swap: after the DOM is replaced we let any hydrators (e.g.
    // the v4 suggestions loader) push a Promise onto `promises` via the
    // `cart-drawer:hydrate` event. When the drawer is already open we
    // wrap in a View Transition so the content swap crossfades; on
    // first-open we skip the transition because it would hide the
    // drawer's CSS slide-in animation behind a full-page snapshot.
    //
    // options.morphFromVariantId — when set, the matching new cart line
    // gets .cd-morph-target so the browser performs a shared-element
    // transition from a pre-tagged .cd-morph-source (e.g. an upsell row
    // already tagged by the caller) into the new line.
    const morphVariantId = options && options.morphFromVariantId ? String(options.morphFromVariantId) : null;

    const swap = async () => {
      this.querySelector('.drawer__inner').classList.contains('is-empty') &&
        this.querySelector('.drawer__inner').classList.remove('is-empty');
      this.productId = parsedState.id;

      // Snapshot existing cart-line <img> elements by variant-id BEFORE
      // innerHTML wipes them. After the swap we re-attach these into the
      // new <li>s, so any image that was already loaded (e.g. an
      // optimistic suggestion-add line, or a line whose qty changed)
      // doesn't have to re-load and never shows a blank slot. The server
      // still drives every other detail of the line — only the <img>
      // node carries forward.
      const preservedImgs = new Map();
      this.querySelectorAll('li.cd-item[data-variant-id]').forEach((li) => {
        const variantId = li.getAttribute('data-variant-id');
        const img = li.querySelector('.cd-item__image');
        if (variantId && img) preservedImgs.set(variantId, img);
      });

      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });

      preservedImgs.forEach((oldImg, variantId) => {
        const newLi = this.querySelector(
          'li.cd-item[data-variant-id="' + CSS.escape(variantId) + '"]'
        );
        if (!newLi) return;
        const newImg = newLi.querySelector('.cd-item__image');
        if (newImg) newImg.replaceWith(oldImg);
      });

      if (morphVariantId) {
        const newLine = this.querySelector(
          'li.cd-item[data-variant-id="' + CSS.escape(morphVariantId) + '"]'
        );
        if (newLine) newLine.classList.add('cd-morph-target');
      }

      const promises = [];
      this.dispatchEvent(new CustomEvent('cart-drawer:hydrate', {
        bubbles: true,
        detail: { promises },
      }));
      if (promises.length) await Promise.all(promises);

      // Fire rendered after hydration is done so any additional
      // listeners see the fully-populated DOM.
      this.dispatchEvent(new CustomEvent('cart-drawer:rendered', { bubbles: true }));
    };

    const isFirstOpen = !this.classList.contains('active');
    const canUseViewTransition = typeof document.startViewTransition === 'function';

    // Force view transition when morphing, even on the rare case of a
    // first-open morph — the shared-element effect is the whole point.
    if ((morphVariantId || !isFirstOpen) && canUseViewTransition) {
      const transition = document.startViewTransition(swap);
      if (morphVariantId) {
        transition.finished.finally(() => {
          this.querySelectorAll('.cd-morph-target').forEach((el) => el.classList.remove('cd-morph-target'));
        });
      }
    } else {
      await swap();
    }

    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    // Defer open to next tick so the swapped DOM paints before the
    // .cd-inner slide transition begins — matches main's setTimeout(0)
    // pattern and ensures the slide is visible to the user.
    //
    // options.noOpen — skip the auto-open. Used by background section
    // refreshes (e.g. cart-state's discount apply/remove) where the
    // user did not request the drawer to be visible. Without this the
    // drawer pops open on every page that auto-promotes a /discount
    // cookie, and again whenever a discount is added/removed in code.
    if (!options || !options.noOpen) {
      setTimeout(() => this.open());
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // Override CartItems.onCartUpdate so that external cartUpdate events (e.g. from
  // the rewards page add-to-cart) refresh the drawer with the cart-drawer section
  // instead of injecting the full cart-page (main-cart-items) content, which would
  // duplicate the empty-cart state and show the wrong structure inside the drawer.
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        // If renderContents() already replaced this element (normal product-form flow),
        // this element is now detached — bail out to avoid a redundant second update.
        if (!this.isConnected) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newCartDrawerDiv = html.querySelector('#CartDrawer');
        const cartDrawerEl = this.closest('cart-drawer');
        if (!newCartDrawerDiv || !cartDrawerEl) return;

        const existingCartDrawerDiv = cartDrawerEl.querySelector('#CartDrawer');
        if (!existingCartDrawerDiv) return;

        const swap = async () => {
          existingCartDrawerDiv.innerHTML = newCartDrawerDiv.innerHTML;

          const newCartDrawerEl = html.querySelector('cart-drawer');
          const isEmpty = newCartDrawerEl ? newCartDrawerEl.classList.contains('is-empty') : false;
          cartDrawerEl.classList.toggle('is-empty', isEmpty);

          const overlay = cartDrawerEl.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', cartDrawerEl.close.bind(cartDrawerEl));

          const promises = [];
          cartDrawerEl.dispatchEvent(new CustomEvent('cart-drawer:hydrate', {
            bubbles: true,
            detail: { promises },
          }));
          if (promises.length) await Promise.all(promises);

          cartDrawerEl.dispatchEvent(new CustomEvent('cart-drawer:rendered', { bubbles: true }));
        };

        if (typeof document.startViewTransition === 'function') {
          document.startViewTransition(swap);
        } else {
          swap();
        }
      })
      .catch(console.error);
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
